import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CertificationQueue, getCertificationQueue } from '../../api/certifications';
import { elapsedSeverityClass, formatElapsed } from '../../utils/elapsed';
import { TableSkeleton } from './CertificationSkeleton';
import './CertificationPage.css';

export const formatDay = (value: string | null | undefined): string => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'n/a';
  return new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

const signedMs = (value: string | null): number => {
  const ms = value ? new Date(value).getTime() : NaN;
  return isNaN(ms) ? 0 : ms;
};

// Open: every case the lawyer put in Certificate, those filed with Finanças
// before first (marked Start here), then oldest first (the backend sorts), with the same coloured waiting badge the lawyer's case list shows. Done: what this certifier
// certified or declined, newest first, as a record only (no link, the case is
// closed to them by then). Nothing else from the case.
const CertificationQueuePage: React.FC = () => {
  const { currentUser } = useAuth();
  const [queue, setQueue] = useState<CertificationQueue | null>(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());

  // Once a minute, so the waiting badges stay current.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!currentUser) return;
      try {
        const token = await currentUser.getIdToken();
        const data = await getCertificationQueue(token);
        if (!cancelled) setQueue(data);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load the queue.');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const items = queue?.items;
  const done = queue?.done;

  return (
    <div className="certification-page">
      <h1>POAs to certify</h1>
      <p className="certification-intro">
        Each case is a power of attorney the customer signed electronically. Open one, compare it with the
        identity document, register the act with the Ordem, then sign and upload the certification.
      </p>
      {error && <div className="certification-error" role="alert">{error}</div>}
      {!error && !queue && <TableSkeleton columns={3} />}
      {queue && <h2 className="certification-section">Open ({items?.length})</h2>}
      {items && items.length === 0 && <p className="certification-empty">Nothing to certify right now.</p>}
      {items && items.length > 0 && (
        <table className="certification-table">
          <thead>
            <tr>
              <th>Signed by</th>
              <th>POA signed</th>
              <th>Waiting</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.case_id}>
                <td>
                  <Link to={`/certifications/${encodeURIComponent(item.case_id)}`}>{item.name || 'Unnamed'}</Link>
                  {item.filed_before && <span className="certification-tag certification-tag--warn">Start here</span>}
                  {item.minor && <span className="certification-tag">Child's case</span>}
                  {item.resigned && <span className="certification-tag certification-tag--warn">Signed again</span>}
                </td>
                <td>{formatDay(item.poa_signed_at)}</td>
                <td>
                  {signedMs(item.poa_signed_at) > 0 ? (
                    <span
                      className={`case-open-elapsed ${elapsedSeverityClass(signedMs(item.poa_signed_at), now)}`}
                      title={`POA signed on ${formatDay(item.poa_signed_at)}`}
                    >
                      Waiting {formatElapsed(signedMs(item.poa_signed_at), now)}
                    </span>
                  ) : (
                    'n/a'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {done && done.length > 0 && (
        <>
          <h2 className="certification-section">Done ({done.length})</h2>
          <table className="certification-table">
            <thead>
              <tr>
                <th>Signed by</th>
                <th>Result</th>
                <th>Date</th>
                <th>Ordem registration</th>
              </tr>
            </thead>
            <tbody>
              {done.map(item => (
                <tr key={item.case_id}>
                  <td>
                    {item.name || 'Unnamed'}
                    {item.minor && <span className="certification-tag">Child's case</span>}
                  </td>
                  <td>{item.status === 'certified' ? 'Certified' : 'Declined'}</td>
                  <td>{formatDay(item.done_at)}</td>
                  <td>{item.registration_number || 'n/a'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default CertificationQueuePage;
